import {
    createSlice,
} from '@reduxjs/toolkit';

import {
    refreshBetslip,
} from '~Betslip/thunks/refreshBetslip';

import {
    updateSelectionBatch,
} from '~Betslip/actions/selectionDetailsActions';

import {
    clearFreebets,
} from '~Betslip/actions/freeBetsActions';

import {
    addSelection,
    removeSelection,
    clearSelections,
    updateSelection,
    updateAllSelections,
    clearPricesByCombinationType,
    initialSelectionData,
    initialState,
} from '~Betslip/actions/userSelectionsActions';

import {
    generateCombinationId,
    isCombinationSelection,
} from '~Betslip/utils/combinationsUtils';

import { GTP_BET_BUILDER_COMBINATION_TYPE } from '~Betslip/uiModes/shared/GTPBetBuilder/constants';

const findSelectionInState = (userSelections, selectionId) => {
    return userSelections.find(item => String(item.id) === String(selectionId));
};

const userSelections = createSlice({
    name: 'betslip/userSelections',
    initialState,
    reducers: {
        addSelections: (state, action) => {
            const {
                selectionIds,
                surveyId,
            } = action.payload;

            const selectionData = selectionIds.map(selectionId => ({
                ...initialSelectionData,
                id: selectionId,
                survey_id: surveyId,
            }));

            return [
                ...state,
                ...selectionData,
            ];
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(
            addSelection,
            (state, action) => {
                const selection = findSelectionInState(state, action.payload.id);
                if (selection) {
                    return;
                }
                state.push(action.payload);
            },
        )
        .addCase(
            removeSelection,
            (state, { payload: selectionIdToRemove }) => state.filter(
                // Ignore combinations - they will be replaced by new ones when betslipFetchData request is fulfilled
                selection => String(selection.id) !== String(selectionIdToRemove),
            ),
        )
        .addCase(
            updateSelection,
            (state, action) => {
                const {
                    id,
                    ...rest
                } = action.payload;
                const selection = findSelectionInState(state, id);
                if (!selection) {
                    return;
                }

                Object.assign(selection, rest);
            },
        )
        .addCase(
            updateAllSelections,
            (state, action) => {
                state.map(
                    selection => Object.assign(selection, action.payload)
                );
            },
        )
        .addCase(
            clearPricesByCombinationType,
            (state, { payload: typeToClear }) => {
                state.map(
                    selection => {
                        const { combination_type } = selection;
                        if (combination_type === typeToClear) {
                            selection.original_price = null;
                        }

                        return selection;
                    }
                );
            },
        )
        .addCase(
            clearSelections,
            () => initialState,
        )
        .addCase(
            refreshBetslip.fulfilled,
            (state, action) => {
                const {
                    payload: {
                        selection_details: singleSelectionDetails,
                        combinations = [],
                        invalidUserSelections,
                    },
                } = action;

                // handle combinations and selections seperately
                const {
                    stateCombinationSelectionsById,
                    stateOtherSelections: newState,
                } = state.reduce(
                    (selections, currentSelection) => {
                        if (isCombinationSelection(currentSelection)) {
                            selections.stateCombinationSelectionsById[currentSelection.id] = currentSelection;
                        } else {
                            selections.stateOtherSelections.push(currentSelection);
                        }

                        return selections;
                    },
                    {
                        stateCombinationSelectionsById: {},
                        stateOtherSelections: [],
                    },
                );

                const combinationSelectionDetails = {};

                combinations.forEach(combination => {
                    const id = generateCombinationId(combination);

                    combinationSelectionDetails[id] = {
                        stake: 0, // set stake first
                        free_bet_id: null,
                        ...(stateCombinationSelectionsById[id] && stateCombinationSelectionsById[id]),
                        combination_type: combination.combination_type,
                        original_price: null,
                        id,
                    };
                    newState.push({ ...combinationSelectionDetails[id] });
                });

                const allSelectionDetails = { ...singleSelectionDetails, ...combinationSelectionDetails };

                return newState
                    .filter(
                        ({ id }) => !invalidUserSelections.includes(id),
                    )
                    .map(
                        (userSelection) => {
                            const { id, original_price } = userSelection;

                            if (original_price !== undefined || !allSelectionDetails[id]) {

                                return userSelection;
                            }

                            const {
                                sp_only,
                                decimal_price,
                            } = allSelectionDetails[id];

                            const sp_selected = !isCombinationSelection(allSelectionDetails[id])
                                && (sp_only || decimal_price === null);
                            const onAddToBetslipData = {
                                sp_selected,
                                original_price: decimal_price,
                            };

                            return {
                                ...userSelection,
                                ...onAddToBetslipData,
                            };
                        },
                    );
            },
        )
        .addCase(
            clearFreebets,
            (state) => (
                 state.map(userSelection => ({
                    ...userSelection,
                    free_bet_id: null,
                 }))
            ),
        )
        .addCase(
            updateSelectionBatch,
            (state, { payload: selectionUpdates }) => {

                // update the original_price if it is not set - required for SelectionPrice price
                // change support
                Object.keys(selectionUpdates).forEach(
                    selectionId => {
                        const { decimal_price, combination_type } = selectionUpdates[selectionId];
                        if (combination_type === GTP_BET_BUILDER_COMBINATION_TYPE && decimal_price) {
                            const userSelection = findSelectionInState(state, selectionId);
                            if (userSelection.original_price === null) {
                                userSelection.original_price = decimal_price;
                            }
                        }
                    },
                );

            },
        );
    },
});

export const {
    addSelections,
} = userSelections.actions;

export default userSelections.reducer;
